import { useCallback, useMemo } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { mapBanners, resolveGmncBannerImage, resolveGmncBannerLink } from "../banners";
import { absUrl, sameOrigin, useSiteOrigin } from "../useSiteOrigin";
import { useSettings } from "../useSettings";
import "./top-banner.css";

function BannerSlide({ banner, origin, onLoad }) {
  const href = banner.cta ? absUrl(origin, banner.cta) : "";
  const external = href ? !sameOrigin(origin, href) : false;
  const image = (
    <img
      src={banner.imageSrc}
      alt={banner.alt}
      loading="eager"
      decoding="async"
      onLoad={onLoad}
    />
  );

  if (!href) {
    return <div className="kbv-top-banner__slide">{image}</div>;
  }

  return (
    <a
      className="kbv-top-banner__slide"
      href={href}
      rel={external ? "nofollow noopener noreferrer" : "nofollow"}
      {...(external ? { target: "_blank" } : {})}
    >
      {image}
    </a>
  );
}

function Gmnc({ imageSrc, href }) {
  if (!imageSrc) return null;
  const image = <img src={imageSrc} alt="GMNC banner" loading="lazy" decoding="async" />;
  if (!href) return <div className="kbv-top-banner__gmnc">{image}</div>;
  return (
    <a className="kbv-top-banner__gmnc" href={href} target="_blank" rel="nofollow noopener noreferrer">
      {image}
    </a>
  );
}

function TopBannerView({ banners, gmncImage, gmncHref, origin }) {
  const loop = banners.length > 1;
  const autoplay = useMemo(
    () => Autoplay({ delay: 4500, stopOnInteraction: false, stopOnMouseEnter: true }),
    []
  );
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop, align: "start" }, [autoplay]);
  const onLoad = useCallback(() => {
    emblaApi?.reInit();
  }, [emblaApi]);

  return (
    <div className="kbv-home kbv-top-banner">
      {banners.length > 0 ? (
        <div className="kbv-top-banner__viewport" ref={emblaRef}>
          <div className="kbv-top-banner__track">
            {banners.map((banner, index) => (
              <BannerSlide
                key={banner.id}
                banner={banner}
                origin={origin}
                onLoad={index === 0 ? onLoad : undefined}
              />
            ))}
          </div>
        </div>
      ) : null}
      <Gmnc imageSrc={gmncImage} href={gmncHref} />
    </div>
  );
}

export default function TopBanner() {
  const origin = useSiteOrigin();
  const { settings, ready } = useSettings();
  const banners = useMemo(() => mapBanners(settings?.top_banner, { sort: true }), [settings]);
  const gmncImage = resolveGmncBannerImage(settings?.gmnc_banner_image);
  const gmncHref = resolveGmncBannerLink(settings?.gmnc_banner_link);

  if (!ready) return null;
  if (!banners.length && !gmncImage) return null;

  return (
    <TopBannerView banners={banners} gmncImage={gmncImage} gmncHref={gmncHref} origin={origin} />
  );
}
